function toNum(value) {
  const n = Number(String(value ?? '').replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
}

function headerIndex(header, names) {
  const lower = header.map((h) => String(h || '').trim().toLowerCase());
  return names.map((n) => lower.indexOf(n)).find((i) => i >= 0) ?? -1;
}

export function parseCsvStrings(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l && !l.startsWith('#'));
  if (!lines.length) return [];
  const rows = lines.map((line) => line.split(/[,;\t]/).map((c) => c.trim()));
  const header = rows[0].map((c) => c.toLowerCase());
  const looksHeader = header.some((h) =>
    /east|north|rl|elev|x|y|z|name|string|poly/.test(h)
  );
  const body = looksHeader ? rows.slice(1) : rows;
  const head = looksHeader ? rows[0] : [];
  const iE = looksHeader ? headerIndex(head, ['east', 'easting', 'e', 'x']) : 0;
  const iN = looksHeader ? headerIndex(head, ['north', 'northing', 'n', 'y']) : 1;
  const iR = looksHeader ? headerIndex(head, ['rl', 'elev', 'elevation', 'z']) : 2;
  const iName = looksHeader ? headerIndex(head, ['name', 'string', 'id', 'polyline']) : 3;
  const groups = new Map();
  body.forEach((cols, idx) => {
    const east = toNum(cols[iE]);
    const north = toNum(cols[iN]);
    const rl = toNum(cols[iR]);
    if (east == null || north == null || rl == null) return;
    const name = (iName >= 0 && cols[iName] ? cols[iName] : 'string') || 'string';
    if (!groups.has(name)) groups.set(name, []);
    groups.get(name).push({ east, north, rl, order: idx });
  });
  return Array.from(groups.entries()).map(([name, points]) => ({
    name,
    closed: false,
    points,
  }));
}

function dxfGroup(code, value) {
  return `${code}\n${value}\n`;
}

export function parseDxfStrings(text) {
  const raw = String(text || '');
  if (!raw.trim()) return [];
  const lines = raw.split(/\r?\n/);
  const pairs = [];
  for (let i = 0; i + 1 < lines.length; i += 2) {
    const code = parseInt(lines[i].trim(), 10);
    if (!Number.isFinite(code)) continue;
    pairs.push({ code, value: lines[i + 1].trim() });
  }
  const strings = [];
  let i = 0;
  while (i < pairs.length) {
    if (pairs[i].code !== 0) {
      i += 1;
      continue;
    }
    const type = String(pairs[i].value || '').toUpperCase();
    i += 1;
    if (type === 'LINE') {
      const pt = { x1: null, y1: null, z1: 0, x2: null, y2: null, z2: 0, layer: 'LINE' };
      while (i < pairs.length && pairs[i].code !== 0) {
        const { code, value } = pairs[i];
        if (code === 8) pt.layer = value;
        if (code === 10) pt.x1 = toNum(value);
        if (code === 20) pt.y1 = toNum(value);
        if (code === 30) pt.z1 = toNum(value) ?? 0;
        if (code === 11) pt.x2 = toNum(value);
        if (code === 21) pt.y2 = toNum(value);
        if (code === 31) pt.z2 = toNum(value) ?? 0;
        i += 1;
      }
      if (pt.x1 != null && pt.y1 != null && pt.x2 != null && pt.y2 != null) {
        strings.push({
          name: pt.layer || 'LINE',
          closed: false,
          points: [
            { east: pt.x1, north: pt.y1, rl: pt.z1 },
            { east: pt.x2, north: pt.y2, rl: pt.z2 },
          ],
        });
      }
      continue;
    }
    if (type === 'POLYLINE' || type === 'LWPOLYLINE') {
      const name = { layer: type, closed: false, points: [] };
      let x = null;
      let y = null;
      let z = 0;
      let flags = 0;
      const flush = () => {
        if (x != null && y != null) name.points.push({ east: x, north: y, rl: z });
        x = null;
        y = null;
      };
      while (i < pairs.length && pairs[i].code !== 0) {
        const { code, value } = pairs[i];
        if (code === 8) name.layer = value;
        if (code === 70) flags = parseInt(value, 10) || 0;
        if (code === 10) {
          if (type === 'LWPOLYLINE' && x != null) flush();
          x = toNum(value);
        }
        if (code === 20) y = toNum(value);
        if (code === 30) z = toNum(value) ?? z;
        if (code === 38) z = toNum(value) ?? z;
        i += 1;
      }
      flush();
      if (name.points.length >= 2) {
        strings.push({
          name: name.layer || type,
          closed: Boolean(flags & 1),
          points: name.points,
        });
      }
      continue;
    }
    if (type === 'VERTEX') {
      i += 1;
    }
  }
  return strings;
}

export function stringsToCsv(strings) {
  const lines = ['name,east,north,rl'];
  (strings || []).forEach((s) => {
    (s.points || []).forEach((p) => {
      lines.push(`${s.name || 'string'},${p.east},${p.north},${p.rl}`);
    });
  });
  return `${lines.join('\n')}\n`;
}

export function stringsToDxf(strings) {
  let body = dxfGroup(0, 'SECTION') + dxfGroup(2, 'ENTITIES');
  (strings || []).forEach((s) => {
    const pts = s.points || [];
    if (pts.length < 2) return;
    body += dxfGroup(0, 'POLYLINE');
    body += dxfGroup(8, s.name || 'MDV');
    body += dxfGroup(66, '1');
    body += dxfGroup(70, s.closed ? '1' : '0');
    pts.forEach((p) => {
      body += dxfGroup(0, 'VERTEX');
      body += dxfGroup(8, s.name || 'MDV');
      body += dxfGroup(10, p.east);
      body += dxfGroup(20, p.north);
      body += dxfGroup(30, p.rl);
    });
    body += dxfGroup(0, 'SEQEND');
  });
  body += dxfGroup(0, 'ENDSEC') + dxfGroup(0, 'EOF');
  return body;
}

export function parseStringFile(name, text) {
  const lower = String(name || '').toLowerCase();
  if (lower.endsWith('.dxf')) return parseDxfStrings(text);
  return parseCsvStrings(text);
}
